/**
 * Cook mode (Steg F4) — a focused, kitchen-friendly view of ONE recipe.
 *
 * Reached from the read view via "Kokemodus" (any member — cooking is a read
 * activity). It is frontend-only over the already-persisted recipe data plus the
 * F6 "Notater" (comments) API, which it reuses as a read-and-add notes drawer.
 *
 * Behaviour (from the design handoff `# deSjeff Cooking Mode Feature`):
 *  - a "Hvor mange porsjoner?" modal gates the start and picks the target
 *    servings; ingredient QUANTITIES then scale against the recipe's own
 *    `servings` (rows with a null quantity render verbatim — never scaled);
 *  - ingredients can be checked off, method steps marked done;
 *  - the screen is kept awake via the Wake Lock API while cooking;
 *  - sections that share a NAME across the ingredient AND step lists become
 *    paired tabs (e.g. "Dressing"/"Salat"); everything unpaired folds into one
 *    "Annet" group. With a single resulting group (the common case) no tabs are
 *    shown — ingredients then steps just stack (mobile-first; the desktop
 *    split-view is Slice 4).
 *
 * Security: all recipe/notes text is rendered as React text nodes (never
 * dangerouslySetInnerHTML). Authorization is the server's (recipe:read to load,
 * recipe:comment to add a note); this component is a UI over the caller's own
 * access — a recipe they may not see already 404s at the read view.
 */

import {
  formatIngredientAmount,
  type IngredientItem,
  type Recipe,
  type RecipeComment,
  type StepItem,
} from "@tjommi/shared";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  NotFoundError,
  UnauthorizedError,
  createComment,
  fetchComments,
  fetchRecipe,
} from "../api";
import { CookNotes } from "../components/CookNotes";

type LoadState =
  | { status: "loading" }
  | { status: "ok"; recipe: Recipe }
  | { status: "not-found" }
  | { status: "error"; message: string };

/** One cook-mode group: paired ingredient + step content under a shared name. */
export interface CookSection {
  /** Display name ("" when the group is the unnamed/leftover "Annet" bucket). */
  name: string;
  ingredientItems: IngredientItem[];
  stepItems: StepItem[];
}

/**
 * Pair ingredient and step sections BY NAME. A trimmed, case-insensitive name
 * present in BOTH lists yields a paired group (in first-seen order). Everything
 * left over (unnamed sections, or a name in only one list) folds into a single
 * trailing "Annet" group. Pure + exported for unit testing.
 */
export function buildCookSections(
  ingredientSections: Recipe["ingredientSections"],
  stepSections: Recipe["stepSections"],
): CookSection[] {
  const key = (name: string): string => name.trim().toLowerCase();
  const ingByKey = new Map<string, { name: string; items: IngredientItem[] }>();
  const order: string[] = [];
  for (const sec of ingredientSections) {
    const k = key(sec.name);
    if (k === "") continue;
    if (!ingByKey.has(k)) {
      ingByKey.set(k, { name: sec.name.trim(), items: [] });
      order.push(k);
    }
    ingByKey.get(k)!.items.push(...sec.items);
  }
  const stepByKey = new Map<string, { name: string; items: StepItem[] }>();
  for (const sec of stepSections) {
    const k = key(sec.name);
    if (k === "") continue;
    if (!stepByKey.has(k)) {
      stepByKey.set(k, { name: sec.name.trim(), items: [] });
      if (!order.includes(k)) order.push(k);
    }
    stepByKey.get(k)!.items.push(...sec.items);
  }

  const sharedKeys = order.filter((k) => ingByKey.has(k) && stepByKey.has(k));
  const shared = new Set(sharedKeys);

  const paired: CookSection[] = sharedKeys.map((k) => ({
    name: ingByKey.get(k)!.name || stepByKey.get(k)!.name,
    ingredientItems: ingByKey.get(k)!.items,
    stepItems: stepByKey.get(k)!.items,
  }));

  // Leftovers keep original document order, ingredient sections then step sections.
  const leftoverIngredients: IngredientItem[] = [];
  for (const sec of ingredientSections) {
    if (!shared.has(key(sec.name))) leftoverIngredients.push(...sec.items);
  }
  const leftoverSteps: StepItem[] = [];
  for (const sec of stepSections) {
    if (!shared.has(key(sec.name))) leftoverSteps.push(...sec.items);
  }

  const groups = [...paired];
  if (leftoverIngredients.length > 0 || leftoverSteps.length > 0) {
    groups.push({
      // With no paired groups this IS the whole recipe → leave the name empty so
      // the caller renders it untitled (no pointless single "Annet" tab).
      name: paired.length === 0 ? "" : "Annet",
      ingredientItems: leftoverIngredients,
      stepItems: leftoverSteps,
    });
  }
  return groups;
}

/** Keep the screen awake while `active`; re-acquire when the tab returns to view. */
function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;
    let sentinel: WakeLockSentinel | null = null;
    let released = false;

    const request = async (): Promise<void> => {
      try {
        sentinel = await navigator.wakeLock.request("screen");
      } catch {
        // Denied / unsupported / not visible — cooking still works, screen may dim.
      }
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible" && !released) void request();
    };

    void request();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}

export function CookModePage({
  accessToken,
  onSessionExpired,
}: {
  accessToken: string;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    if (!id) return;
    let ignore = false;
    setState({ status: "loading" });
    fetchRecipe(id, accessToken)
      .then((recipe) => {
        if (!ignore) setState({ status: "ok", recipe });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) return onSessionExpired();
        if (err instanceof NotFoundError) return setState({ status: "not-found" });
        setState({ status: "error", message: err instanceof Error ? err.message : "Ukjent feil" });
      });
    return () => {
      ignore = true;
    };
  }, [id, accessToken]);

  if (state.status === "loading") {
    return (
      <main className="cook" data-testid="cook-loading">
        <p className="cook-msg">Laster oppskrift …</p>
      </main>
    );
  }
  if (state.status === "not-found") {
    return (
      <main className="cook" data-testid="cook-not-found">
        <p className="cook-msg">Fant ikke oppskriften.</p>
        <Link to="/" className="btn btn-ghost">
          Til oversikten
        </Link>
      </main>
    );
  }
  if (state.status === "error") {
    return (
      <main className="cook" data-testid="cook-error">
        <p className="cook-msg">Kunne ikke laste oppskriften. Prøv å laste siden på nytt.</p>
      </main>
    );
  }

  return (
    <CookSession
      recipe={state.recipe}
      accessToken={accessToken}
      onSessionExpired={onSessionExpired}
      onExit={() => navigate(`/recipes/${state.recipe.id}`)}
    />
  );
}

function CookSession({
  recipe,
  accessToken,
  onSessionExpired,
  onExit,
}: {
  recipe: Recipe;
  accessToken: string;
  onSessionExpired: () => void;
  onExit: () => void;
}): React.JSX.Element {
  const base = recipe.servings != null && recipe.servings >= 1 ? recipe.servings : null;
  const canScale = base != null;

  // No base servings ⇒ nothing to scale ⇒ skip the portions modal entirely.
  const [started, setStarted] = useState(!canScale);
  const [portions, setPortions] = useState(base ?? 2);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [marked, setMarked] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState(0);
  const [notesOpen, setNotesOpen] = useState(false);
  const [showImages, setShowImages] = useState(true);

  // Notes (F6 comments) are fetched ONCE here so the header badge count and the
  // drawer list stay in sync; adding a note updates both.
  const [notes, setNotes] = useState<RecipeComment[]>([]);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [notesError, setNotesError] = useState(false);

  useEffect(() => {
    let ignore = false;
    setNotesLoaded(false);
    setNotesError(false);
    fetchComments(recipe.id, accessToken)
      .then((n) => {
        if (ignore) return;
        setNotes(n);
        setNotesLoaded(true);
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) return onSessionExpired();
        setNotesError(true);
        setNotesLoaded(true);
      });
    return () => {
      ignore = true;
    };
  }, [recipe.id, accessToken]);

  const addNote = async (body: string): Promise<void> => {
    const created = await createComment(recipe.id, body, accessToken);
    setNotes((prev) => [...prev, created]);
  };

  useWakeLock(started);

  const groups = useMemo(
    () => buildCookSections(recipe.ingredientSections, recipe.stepSections),
    [recipe],
  );
  const showTabs = groups.length > 1;
  const current = groups[Math.min(activeTab, Math.max(groups.length - 1, 0))] ?? {
    name: "",
    ingredientItems: [],
    stepItems: [],
  };
  const factor = canScale ? portions / base : 1;
  const hasStepImages = current.stepItems.some((s) => Boolean(s.imageUrl));

  // Global, continuous step numbers across ALL groups (matches the read view).
  const stepNumbers = useMemo(() => {
    const map = new Map<string, number>();
    let n = 0;
    for (const g of groups) for (const s of g.stepItems) map.set(s.id, ++n);
    return map;
  }, [groups]);

  const clampPortions = (n: number): number => Math.min(Math.max(n, 1), 99);

  if (!started) {
    return (
      <div className="cook-modal-backdrop" data-testid="cook-portions-modal">
        <div className="cook-modal">
          <h2>Hvor mange porsjoner?</h2>
          <p>Velg antall porsjoner, så skaleres ingrediensene automatisk.</p>
          <div className="cook-portions-input">
            <button
              type="button"
              aria-label="Færre porsjoner"
              data-testid="cook-portions-minus"
              onClick={() => setPortions((p) => clampPortions(p - 1))}
            >
              −
            </button>
            <span data-testid="cook-portions-value">{portions}</span>
            <button
              type="button"
              aria-label="Flere porsjoner"
              data-testid="cook-portions-plus"
              onClick={() => setPortions((p) => clampPortions(p + 1))}
            >
              +
            </button>
          </div>
          <button
            type="button"
            className="btn btn-primary cook-start"
            data-testid="cook-start"
            onClick={() => setStarted(true)}
          >
            Start laging
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="cook-cancel"
            onClick={onExit}
          >
            Avbryt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cook" data-testid="cook-session">
      <header className="cook-head">
        <div className="cook-head-left">
          <button
            type="button"
            className="btn btn-ghost cook-exit"
            data-testid="cook-exit"
            onClick={onExit}
          >
            ✕ Avslutt
          </button>
          <h1 className="cook-title">{recipe.title}</h1>
        </div>
        <div className="cook-head-right">
          {canScale && (
            <div className="cook-portions-adjuster" data-testid="cook-portions-adjuster">
              <button
                type="button"
                aria-label="Færre porsjoner"
                data-testid="cook-adj-minus"
                onClick={() => setPortions((p) => clampPortions(p - 1))}
              >
                −
              </button>
              <span data-testid="cook-adj-value">{portions} porsjoner</span>
              <button
                type="button"
                aria-label="Flere porsjoner"
                data-testid="cook-adj-plus"
                onClick={() => setPortions((p) => clampPortions(p + 1))}
              >
                +
              </button>
            </div>
          )}
          <button
            type="button"
            className="cook-notes-toggle"
            data-testid="cook-notes-toggle"
            aria-expanded={notesOpen}
            aria-label={notes.length > 0 ? `Notater (${notes.length})` : "Notater"}
            onClick={() => setNotesOpen((o) => !o)}
          >
            Notater
            {notesLoaded && !notesError && notes.length > 0 && (
              <span className="cook-notes-badge" data-testid="cook-notes-badge" aria-hidden="true">
                {notes.length}
              </span>
            )}
          </button>
        </div>
      </header>

      {showTabs && (
        <div className="cook-tabs" role="tablist" data-testid="cook-tabs">
          {groups.map((g, i) => (
            <button
              key={g.name || `tab-${i}`}
              type="button"
              role="tab"
              aria-selected={i === activeTab}
              className={`cook-tab${i === activeTab ? " active" : ""}`}
              data-testid={`cook-tab-${i}`}
              onClick={() => setActiveTab(i)}
            >
              {g.name || "Annet"}
            </button>
          ))}
        </div>
      )}

      <div className="cook-body">
        <section className="cook-ingredients" aria-label="Ingredienser">
          <h2>Ingredienser</h2>
          {current.ingredientItems.length === 0 && (
            <p className="cook-msg">Ingen ingredienser her.</p>
          )}
          <ul className="cook-ing-list">
            {current.ingredientItems.map((it) => {
              const scaled =
                it.quantity == null ? null : Math.round(it.quantity * factor * 100) / 100;
              const isChecked = checked[it.id] ?? false;
              return (
                <li key={it.id}>
                  <button
                    type="button"
                    className={`cook-ing${isChecked ? " checked" : ""}`}
                    data-testid={`cook-ing-${it.id}`}
                    aria-pressed={isChecked}
                    onClick={() => setChecked((c) => ({ ...c, [it.id]: !isChecked }))}
                  >
                    <span className="cook-ing-check" aria-hidden="true">
                      {isChecked ? "✓" : ""}
                    </span>
                    <span className="cook-ing-amount">
                      {formatIngredientAmount({ quantity: scaled, unit: it.unit })}
                    </span>
                    <span className="cook-ing-name">{it.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="cook-steps" aria-label="Fremgangsmåte">
          <div className="cook-steps-head">
            <h2>Fremgangsmåte</h2>
            {hasStepImages && (
              <button
                type="button"
                className="cook-notes-toggle"
                data-testid="cook-images-toggle"
                aria-pressed={showImages}
                onClick={() => setShowImages((v) => !v)}
              >
                {showImages ? "Skjul bilder" : "Vis bilder"}
              </button>
            )}
          </div>
          {current.stepItems.length === 0 && <p className="cook-msg">Ingen steg her.</p>}
          <ol className="cook-step-list">
            {current.stepItems.map((s) => {
              const isMarked = marked[s.id] ?? false;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`cook-step${isMarked ? " marked" : ""}`}
                    data-testid={`cook-step-${s.id}`}
                    aria-pressed={isMarked}
                    onClick={() => setMarked((m) => ({ ...m, [s.id]: !isMarked }))}
                  >
                    <span className="cook-step-row">
                      <span className="cook-step-n" aria-hidden="true">
                        {stepNumbers.get(s.id)}
                      </span>
                      <span className="cook-step-text">{s.text}</span>
                    </span>
                    {showImages && s.imageUrl && (
                      <img
                        className="cook-step-img"
                        src={s.imageUrl}
                        alt=""
                        data-testid={`cook-step-img-${s.id}`}
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        </section>
      </div>

      {notesOpen && (
        <CookNotes
          notes={notes}
          loaded={notesLoaded}
          error={notesError}
          onAdd={addNote}
          onClose={() => setNotesOpen(false)}
          onSessionExpired={onSessionExpired}
        />
      )}
    </div>
  );
}
